import { forwardRef, useMemo, useRef, type ComponentPropsWithoutRef } from "react";
import * as DialogPrimitiveLib from "@radix-ui/react-dialog";

import { cn, platformProps } from "../../lib";
import { useScrollOverflow } from "../../lib/scroll-overflow";
import { composeRefs } from "../slot";
import type {
  DialogBodyPrimitiveProps,
  DialogClosePrimitiveProps,
  DialogContentPrimitiveProps,
  DialogDescriptionPrimitiveProps,
  DialogOverlayPrimitiveProps,
  DialogPortalPrimitiveProps,
  DialogRootPrimitiveProps,
  DialogTitlePrimitiveProps,
} from "./types";

/** Ref types, resolved per platform like the components. */
export type DialogOverlayRef = HTMLDivElement;
export type DialogContentRef = HTMLDivElement;
export type DialogTitleRef = HTMLHeadingElement;
export type DialogDescriptionRef = HTMLParagraphElement;
export type DialogBodyRef = HTMLDivElement;
export type DialogCloseRef = HTMLButtonElement;

/**
 * Dialog Root - Web (Radix)
 *
 * Controlled and uncontrolled both supported. Styling is owned by the
 * molecule layer via className on Overlay / Content.
 */
const DialogRoot = ({ open, defaultOpen, onOpenChange, children }: DialogRootPrimitiveProps) => (
  <DialogPrimitiveLib.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
    {children}
  </DialogPrimitiveLib.Root>
);

DialogRoot.displayName = DialogPrimitiveLib.Root.displayName;

/**
 * Dialog Portal - Web (Radix)
 *
 * `portalHost` is accepted for cross-platform parity but ignored on web —
 * Radix manages portal targets internally.
 */
const DialogPortal = ({ children }: DialogPortalPrimitiveProps) => (
  <DialogPrimitiveLib.Portal>{children}</DialogPrimitiveLib.Portal>
);

DialogPortal.displayName = DialogPrimitiveLib.Portal.displayName;

/**
 * Dialog Overlay - Web (Radix)
 *
 * Receives state-driven animation classes from Radix `data-state` attrs.
 * The molecule layer composes the visual overlay color via className.
 * `onPress` and `disableDismiss` are native-only: Content's `disableDismiss`
 * blocks outside clicks on web.
 */
const DialogOverlay = forwardRef<DialogOverlayRef, DialogOverlayPrimitiveProps>(
  (
    {
      className,
      testID,
      children,
      onPress: _onPress,
      disableDismiss: _disableDismiss,
      ...injected
    },
    ref,
  ) => (
    <DialogPrimitiveLib.Overlay
      ref={ref}
      className={className}
      {...platformProps({ testID, slot: "dialog-overlay" })}
      {...injected}
    >
      {children}
    </DialogPrimitiveLib.Overlay>
  ),
);

DialogOverlay.displayName = DialogPrimitiveLib.Overlay.displayName;

type InjectedHandlers = Pick<
  ComponentPropsWithoutRef<typeof DialogPrimitiveLib.Content>,
  "onPointerDownOutside" | "onEscapeKeyDown" | "onInteractOutside" | "onCloseAutoFocus"
>;

/**
 * Dialog Content - Web (Radix)
 *
 * Wires `disableDismiss` to Radix's `onPointerDownOutside` and
 * `onEscapeKeyDown` so the molecule can enforce `dismissible={false}` without
 * each consumer reimplementing the guards.
 *
 * On close, focus returns to the element that opened the dialog. Radix only
 * returns it to its own Trigger, and our dialogs open from outside buttons,
 * so focus fell to the page. The opener is recorded on the first render,
 * before a child can take focus (an autofocusing field also makes Radix skip
 * `onOpenAutoFocus`).
 */
const DialogContent = forwardRef<DialogContentRef, DialogContentPrimitiveProps>(
  (
    { className, children, testID, accessibilityLabel, disableDismiss = false, ...injected },
    ref,
  ) => {
    // An injected outside/Escape handler still runs, but after the guard, which
    // it can't replace
    const { onPointerDownOutside, onEscapeKeyDown, onInteractOutside, onCloseAutoFocus, ...rest } =
      injected as InjectedHandlers;
    const opener = useRef<Element | null | undefined>(undefined);
    if (opener.current === undefined) opener.current = document.activeElement;
    return (
      <DialogPrimitiveLib.Content
        ref={ref}
        aria-label={accessibilityLabel}
        className={className}
        {...platformProps({ testID, slot: "dialog-content" })}
        {...rest}
        onPointerDownOutside={(event) => {
          if (disableDismiss) event.preventDefault();
          onPointerDownOutside?.(event);
        }}
        onEscapeKeyDown={(event) => {
          if (disableDismiss) event.preventDefault();
          onEscapeKeyDown?.(event);
        }}
        onInteractOutside={(event) => {
          if (disableDismiss) event.preventDefault();
          onInteractOutside?.(event);
        }}
        onCloseAutoFocus={(event) => {
          // An injected handler runs first and can keep focus where it puts it
          onCloseAutoFocus?.(event);
          if (event.defaultPrevented) return;
          const target = opener.current;
          if (target instanceof HTMLElement && target !== document.body && target.isConnected) {
            event.preventDefault();
            target.focus();
          }
        }}
      >
        {children}
      </DialogPrimitiveLib.Content>
    );
  },
);

DialogContent.displayName = DialogPrimitiveLib.Content.displayName;

/**
 * Dialog Title - Web (Radix)
 *
 * Renders an `h2` element with `aria-labelledby` auto-wired by Radix to the
 * parent `Content`'s dialog role.
 */
const DialogTitle = forwardRef<DialogTitleRef, DialogTitlePrimitiveProps>(
  ({ asChild, className, children, ...injected }, ref) => (
    <DialogPrimitiveLib.Title ref={ref} asChild={asChild} className={className} {...injected}>
      {children}
    </DialogPrimitiveLib.Title>
  ),
);

DialogTitle.displayName = DialogPrimitiveLib.Title.displayName;

/**
 * Dialog Description - Web (Radix)
 *
 * Auto-wired via `aria-describedby` on the parent Content.
 */
const DialogDescription = forwardRef<DialogDescriptionRef, DialogDescriptionPrimitiveProps>(
  ({ asChild, className, children, ...injected }, ref) => (
    <DialogPrimitiveLib.Description ref={ref} asChild={asChild} className={className} {...injected}>
      {children}
    </DialogPrimitiveLib.Description>
  ),
);

DialogDescription.displayName = DialogPrimitiveLib.Description.displayName;

/**
 * Dialog Body - Web
 *
 * Grows into the surface's free height and scrolls past its maximum, with
 * header and footer pinned.
 */
const DialogBody = forwardRef<DialogBodyRef, DialogBodyPrimitiveProps>(
  ({ className, children, onOverflowChange }, ref) => {
    const viewportRef = useRef<HTMLDivElement>(null);
    const composedRef = useMemo(() => composeRefs(ref, viewportRef), [ref]);
    useScrollOverflow(viewportRef, onOverflowChange);
    return (
      <div ref={composedRef} className={cn("flex-1 overflow-y-auto", className)}>
        {children}
      </div>
    );
  },
);

DialogBody.displayName = "DialogBody";

/**
 * Dialog Close - Web (Radix)
 */
const DialogClose = forwardRef<DialogCloseRef, DialogClosePrimitiveProps>(
  ({ asChild, className, children, testID, accessibilityLabel, ...injected }, ref) => (
    <DialogPrimitiveLib.Close
      ref={ref}
      asChild={asChild}
      // Only when set: with asChild an undefined aria-label reaches the child's
      // props and can override the label it renders itself
      {...(accessibilityLabel !== undefined && { "aria-label": accessibilityLabel })}
      className={className}
      {...platformProps({ testID, slot: "dialog-close" })}
      {...injected}
    >
      {children}
    </DialogPrimitiveLib.Close>
  ),
);

DialogClose.displayName = DialogPrimitiveLib.Close.displayName;

export {
  DialogRoot,
  DialogPortal,
  DialogOverlay,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogClose,
};
